// API Response interface (matches backend response)
export interface IDocumentApiResponse {
	documentId: number;
	participantId: number;
	displayName: string;
	s3Url: string;
	notes: string | null;
	createdOn: string; // ISO date string
	updatedOn: string; // ISO date string
}

// Frontend interface (normalized)
export interface IDocument {
	id?: number | null;
	participantId: number;
	name: string; // Display name
	fileName: string; // Extracted from displayName or s3Url
	fileSize?: number; // in bytes (may not be available)
	fileType?: string; // MIME type (extracted from fileName)
	uploadedOn: string; // ISO date string (from createdOn)
	uploadedBy?: number | null;
	content?: string; // base64 encoded file content (not in API response)
	s3Url?: string; // S3 URL from API
	notes?: string | null; // Notes about the document
}

export class Document implements IDocument {
	id: number | null;
	participantId: number;
	name: string;
	fileName: string;
	fileSize?: number;
	fileType?: string;
	uploadedOn: string;
	uploadedBy: number | null;
	content?: string;
	s3Url?: string;
	notes?: string | null;

	constructor(params: IDocument) {
		this.id = params.id ?? null;
		this.participantId = params.participantId;
		this.name = params.name;
		this.fileName = params.fileName;
		this.fileSize = params.fileSize;
		this.fileType = params.fileType;
		this.uploadedOn = params.uploadedOn;
		this.uploadedBy = params.uploadedBy ?? null;
		this.content = params.content;
		this.s3Url = params.s3Url;
		this.notes = params.notes ?? null;
	}

	// Helper method to create from API response
	static fromApiResponse(apiDoc: IDocumentApiResponse): IDocument {
		// Extract file name from displayName or s3Url
		const fullFileName = apiDoc.displayName || apiDoc.s3Url.split("/").pop() || "document";
		const fileExtension = fullFileName.split(".").pop()?.toLowerCase() || "";

		// Determine MIME type from extension
		const mimeTypes: { [key: string]: string } = {
			pdf: "application/pdf",
			doc: "application/msword",
			docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
			jpg: "image/jpeg",
			jpeg: "image/jpeg",
			png: "image/png",
			gif: "image/gif",
		};

		return {
			id: apiDoc.documentId,
			participantId: apiDoc.participantId,
			name: apiDoc.displayName,
			fileName: fullFileName,
			fileType: mimeTypes[fileExtension] || "application/octet-stream",
			uploadedOn: apiDoc.createdOn,
			s3Url: apiDoc.s3Url,
			notes: apiDoc.notes,
		};
	}
}
